/** An API failure with FastAPI's `detail` turned into one readable message. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, detail: unknown, fallback = 'Request failed') {
    super(messageFromDetail(detail) ?? `${fallback} (HTTP ${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

interface ValidationIssue {
  loc?: Array<string | number>;
  msg?: string;
}

/**
 * FastAPI errors are `{"detail": "text"}`, or for 422 `{"detail": [{loc, msg, type}]}`.
 * Accepts the whole body or the detail itself.
 */
export function messageFromDetail(body: unknown): string | undefined {
  const detail =
    body && typeof body === 'object' && 'detail' in body
      ? (body as { detail: unknown }).detail
      : body;
  if (typeof detail === 'string') return detail || undefined;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d: ValidationIssue) => {
        const field = (d.loc ?? []).filter((l) => l !== 'body').join('.');
        return field ? `${field}: ${d.msg ?? 'invalid'}` : d.msg;
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : undefined;
  }
  return undefined;
}

/** Network failures (no response) become an ApiError with status 0. */
export function toApiError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  const message = e instanceof Error ? e.message : String(e);
  return new ApiError(0, `Could not reach the server: ${message}`);
}
